import { PSBErrors } from "@/constants/errors/product-slot-booking";
import {
  AdyenResponseStatus,
  DiscountType,
  IAdyenResponseStatusTrack,
  IBookingDiscount,
  IBookingDiscountTrack,
  IBookingPayment,
  IBookingPaymentTrack,
  IBookingsPendingSync,
  IBookingsSteps,
  IBookingState,
  IConfirmedAmount,
  IPromocodeTrack,
  IPSSDiscount,
  ISynchingReason,
  RefundListenerTrack,
  SyncType,
  BookingChange,
  BookingChangesTrack,
} from "@/interfaces/store/IBooking";
import {
  IBookingCustomerInfo,
  IProductSlotBooking,
  BaseOrderSession,
} from "@/types/db/product-slot-booking";
import {
  BookingStatus,
  IAvailabilityInfo,
  IProductSlotState,
  IPSSBalance,
  IRemovePSS,
  PaymentStatus,
  PSSType,
} from "@/types/db/product-slot-state";
import {
  completeBooking,
  createBooking,
} from "@/utils/helpers/product-slot-booking";
import {
  getPSSValue,
  releaseTimeslots,
  removePSSFromPSB,
} from "@/utils/helpers/product-slot-state";
import { getTimeslotsByIds } from "@/dexie/timeslots";
import { cloneDeep, pull } from "lodash";
import {
  getModule,
  VuexModule,
  Mutation,
  Action,
  Module,
} from "vuex-module-decorators";
import store from "..";
import { BookingStep } from "@/constants/booking";
import {
  AccountingState,
  IAccounting,
  IAccountingInfo,
  PaymentCard,
  PaymentMethods,
  PaymentTool,
  TransactionTypes,
} from "@/types/db/accounting";
import { Currencies, Currency } from "@/constants/currency";
import { generateID } from "@/utils/helpers/app";
import {
  completeAccountingsBooking,
  completeBookingPayment,
} from "@/utils/helpers/accounting";
import {
  bookings,
  clearCurrentSessionExternalVouchers,
  groupedPSS,
  maxBookingTabsToShow,
} from "@/utils/vue-helpers/booking";
import { sellOnSoldOut } from "@/utils/helpers/product-category";
import { getProductCategoryByPriceId } from "@/dexie/product-category";
import { IProductCategory } from "@/types/db/product-category";
import { LogLevel, LogService } from "@/services/logging";
import dayjs from "dayjs";
import { roundToNearestMultipleOf5, sumArray } from "@/utils/helpers/common";
import { IPromocode } from "@/api/promocodes/promocodes";
import { IAccountingEntry } from "@/types/db/accounting-entry";
import PSSModule from "./pss";
import { startTransaction } from "@/utils/helpers/fiskaly";
import POSConfigModule from "./pos-config";
import { CashAmounts } from "@/interfaces/cash-float/cash-float";
import { makeHoldTokenExpire } from "@/api/seats-io/seats-io";
import { OrderSessionState } from "@smeetz/order-common";
import ExternalVouchersModule from "./external-vouchers";
import { applyOffersToBooking } from "@/utils/helpers/offers";
import OffersModule from "./offers";
import {
  processAdditionalEvents,
  processStartSaleEvent,
} from "@/utils/helpers/compliance";
import { EventType } from "@smeetz/compliance-common";
import { startRefundListener } from "@/services/sync-service";
import {
  isFrenchCompliant,
  isGermanCompliant,
} from "@/utils/vue-helpers/compliance";
import {
  IBookingWaiverTrack,
  ISignedWaiver,
} from "@/interfaces/store/IBooking";

@Module({ dynamic: true, namespaced: true, store, name: "Booking" })
class BookingStoreModule extends VuexModule implements IBookingState {
  public bookings: IBookingState["bookings"] = {};
  public bookingList: IBookingState["bookingList"] = [];
  public isTabAnimated = false;
  // public bookingStep: BookingStep = BookingStep.Order;
  public bookingsSteps: IBookingsSteps = {};
  // public isLoading = false;
  public selectedBookingUUID = "";
  public bookingsAmountsTrack: IBookingPaymentTrack = {};
  public bookingsDiscountsTrack: IBookingDiscountTrack = {};
  public bookingAvailabilityInfo: IAvailabilityInfo[] = [];
  // public payingCurrency = Currencies.CHF;
  // The below array keeps the relationship between a promocode and a booking, used to calculate the discount for the UI and for the moment we create accountings
  // We add one item when a promocode is valid for a given booking
  // We remove one item when the booking is completed/cancelled
  public promocodes: IPromocodeTrack[] = [];
  // The below will contian pending bookings synched to backend, they are copies of what backend has. We will need them to build Patch synchronize-bookings payload
  // the whole scenario is needed when a booking has a promocode applied or when we're editing an order that we pulled from the backend
  //
  public adyenResponseStatusTrack: IAdyenResponseStatusTrack = {};

  public bookingsPendingSynchronization: IBookingsPendingSync = {};

  public calculatorValue = "0";

  public selectedCashCurrency: Currency | null = null;

  public currentOrderSessionId: string | null = null;

  public recentTabNames: string[] = [];

  public currentBookingCount = 0;

  public promocodeBeingChecked = false;

  public refundListeners: RefundListenerTrack = {};

  /**
   * Ticket removals / quantity decreases per booking, reported to the Belgian
   * FDM in the sale event. Only written when Belgian compliance is active.
   */
  public bookingChanges: BookingChangesTrack = {};

  logGettersPerformance = false;

  public bookingsWaiversTrack: { [bookingUUID: string]: IBookingWaiverTrack } =
    {};

  get selectedBookingLinkedWaivers(): ISignedWaiver[] {
    return (
      this.bookingsWaiversTrack[this.selectedBookingUUID]?.linkedWaivers || []
    );
  }

  get getSeatsIoHoldToken(): any | null {
    if (!this.selectedBooking) return null;
    if (!this.selectedBooking.seatsIoHoldToken) return null;
    return this.selectedBooking?.seatsIoHoldToken;
  }

  /**
   * Checks whether or not the selected booking has Acompte type accountings or not
   */
  get isAcompteBooking(): boolean {
    const dt1 = new Date();
    const booking = this.selectedBooking;
    if (!booking) {
      return false;
      // Don't throw a fake error
      // throw new Error(PSBErrors.NoBookingInModule);
    }
    const isAcompte = false;
    const accEntries = booking.accountings
      .filter((ac) => ac.state === AccountingState.Completed)
      .map((ac) => ac.accountingEntriesList)
      .flat();

    for (let i = 0; i < accEntries.length; i++) {
      if (accEntries[i].acompteConfigId != null) {
        const dt2 = new Date();
        if (this.logGettersPerformance) {
          console.log(
            "isAcompteBooking: ",
            (dt2.getTime() - dt1.getTime()) / 1000
          );
        }
        return true;
      }
    }
    const dt2 = new Date();
    if (this.logGettersPerformance) {
      console.log("isAcompteBooking: ", (dt2.getTime() - dt1.getTime()) / 1000);
    }
    return isAcompte;
  }

  /**
   * Whether or not the current payment supports split payment
   * For now, we accept split payment when editing an order
   * You can add more cases to the return statement
   */
  get doesSupportSplitPayment() {
    return true;
  }

  /**
   * Returns {splitPaymentAmount} split amount supplied by the user, rounded to a multiple of 5 if main currency is CHF
   * and {splitAmountProvided} which the same amount without any rounding operations
   */
  get splitAmount() {
    const dt1 = new Date();
    if (this.bookingsAmountsTrack[this.selectedBookingUUID]) {
      const splitPaymentAmount =
        this.bookingsAmountsTrack[this.selectedBookingUUID]
          .splitPaymentAmount || 0;
      const splitAmountProvided =
        this.bookingsAmountsTrack[this.selectedBookingUUID]
          .splitAmountProvided || 0;
      const dt2 = new Date();
      if (this.logGettersPerformance) {
        console.log("splitAmount: ", (dt2.getTime() - dt1.getTime()) / 1000);
      }
      return { splitPaymentAmount, splitAmountProvided };
    }
    const dt2 = new Date();
    if (this.logGettersPerformance) {
      console.log("splitAmount: ", (dt2.getTime() - dt1.getTime()) / 1000);
    }
    return null;
  }

  /**
   * returns a list of balance of each pss (only confirmed accountings)
   */
  get pssListBalances(): IPSSBalance[] {
    const dt1 = new Date();
    const balances: IPSSBalance[] = [];
    const booking = this.selectedBooking;
    if (!booking) {
      const dt2 = new Date();
      if (this.logGettersPerformance) {
        console.log(
          "pssListBalances: ",
          (dt2.getTime() - dt1.getTime()) / 1000
        );
      }
      return [];
    }

    // We consider what the user has paid
    const completedAccountings: IAccounting[] = booking.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Completed &&
        acc.transactionType !== TransactionTypes.TRANSACTION_TYPE_TAX
    );
    // const taxAccEntries =
    //   booking.accountings
    //     .filter(
    //       (acc) =>
    //         acc.state === AccountingState.Completed &&
    //         acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TAX
    //     )
    //     .map((acc) => acc.accountingEntriesList)
    //     .flat() || [];

    const completedAccEntries: IAccountingEntry[] = completedAccountings
      .map((acc) => acc.accountingEntriesList)
      .flat();
    // completedAccEntries.push(
    //   ...taxAccEntries.filter((ae) => ae.countInBalance === TaxType.ExcludedTax)
    // );
    // Lets build the balance by looping thru pssList
    const pssList = booking.pssList;
    for (let i = 0; i < pssList.length; i++) {
      const balance: IPSSBalance = {
        pssUuid: pssList[i].uuid,
        balance: 0,
        pss: pssList[i],
        accEntries: [],
      };
      const pssAccEntries = completedAccEntries.filter(
        (ae) => ae.productSlotStateUuid === pssList[i].uuid
      );
      let balanceValue = 0;
      for (const ae of pssAccEntries) {
        balanceValue += ae.value;
        // we consider excluded tax in the balance
        if (ae.salesTaxMicroValue && ae.salesTaxIncluded === 0) {
          balanceValue += ae.salesTaxMicroValue / 10000;
        }
      }
      balance.balance = balanceValue;
      balance.accEntries = pssAccEntries;
      balances.push(balance);
    }
    const dt2 = new Date();
    if (this.logGettersPerformance) {
      console.log("pssListBalances: ", (dt2.getTime() - dt1.getTime()) / 1000);
    }
    return balances;
  }

  /**
   * Paid amount based on sum of accEntries of accountings of type PAYMENT and DISCOUNT
   */
  get sumPaymentAndDiscountAccEntries(): number {
    const dt1 = new Date();
    if (!this.selectedBooking) {
      return 0;
    }
    const paymentAccEntries = this.selectedBooking?.accountings
      .filter(
        (acc) =>
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT ||
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT
        // acc.transactionType === TransactionTypes.TRANSACTION_TYPE_TAX
      )
      .map((acc) => acc.accountingEntriesList.map((ae) => ae.value))
      .flat();
    const b = sumArray(paymentAccEntries);
    const dt2 = new Date();
    if (this.logGettersPerformance) {
      console.log(
        "sumPaymentAndDiscountAccEntries: ",
        (dt2.getTime() - dt1.getTime()) / 1000
      );
    }
    return b;
  }

  /**
   * Returns the sum of discount accountings
   */
  get sumDiscountAccEntries(): number {
    const dt1 = new Date();
    if (!this.selectedBooking) {
      return 0;
    }
    const discountAccEntries = this.selectedBooking?.accountings
      .filter(
        (acc) =>
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_DISCOUNT
      )
      .map((acc) => acc.accountingEntriesList.map((ae) => ae.value))
      .flat();
    const b = sumArray(discountAccEntries);
    const dt2 = new Date();
    if (this.logGettersPerformance) {
      console.log(
        "sumDiscountAccEntries: ",
        (dt2.getTime() - dt1.getTime()) / 1000
      );
    }
    return b;
  }

  /**
   * Whether or not the current booking has a promocode attached to it or not
   */
  get isPromocodeApplied(): boolean {
    const index = this.promocodes.findIndex(
      (el) => el.bookingUuid === BookingModule.selectedBookingUUID
    );
    if (index >= 0) {
      return true;
    }
    return false;
  }

  /**
   * Gets the promocode info applied to the current booking
   */
  get appliedPromocode(): IPromocodeTrack | null {
    return (
      this.promocodes.find(
        (el) => el.bookingUuid === BookingModule.selectedBookingUUID
      ) || null
    );
  }

  /**
   * Gets the promocodes info applied to the current booking
   */
  get appliedPromocodes(): IPromocodeTrack[] {
    return (
      this.promocodes.filter(
        (el) => el.bookingUuid === BookingModule.selectedBookingUUID
      ) || null
    );
  }

  public get getCalculatorValue(): string {
    return this.calculatorValue;
  }

  get selectedBooking(): IProductSlotBooking | null {
    return this.bookings[this.selectedBookingUUID] || null;
  }

  get bookingStep(): BookingStep {
    const step =
      this.selectedBooking?.step ??
      this.bookingsSteps[this.selectedBookingUUID];
    if (!step) {
      LogService.info(
        `L3-5844 BookingModule: No booking step found for UUID ${this.selectedBookingUUID}`,
        {
          selectedBookingUUID: this.selectedBookingUUID,
          bookingsSteps: this.bookingsSteps,
        },
        LogLevel.Error
      );

      // Fallback to Order step if no step is found
      return BookingStep.Order;
    }
    return step;
  }

  /**
   * Checks whether selected booking has selected tickets or not
   * if a booking contains only abandoned tickets then its considered an empty booking
   */
  get hasTickets(): boolean {
    const booking = this.selectedBooking;

    if (!booking) {
      return false;
    }
    if (booking.pssList.length === 0) {
      return false;
    }
    let hasOnlyAbandonedTickets = true;
    for (let i = 0; i < booking.pssList.length; i++) {
      const pss = booking.pssList[i];
      if (pss.bookingStatus !== BookingStatus.Abandoned) {
        hasOnlyAbandonedTickets = false;
      }
    }
    return booking.pssList.length > 0 && !hasOnlyAbandonedTickets;
  }

  get isBookingCoveredByAvailabilities(): boolean {
    // compare all prices of the booking if they have enough availabilities in local db
    // to do that use groupedPSS() method of asiderOrderSummary, it has pss grouped by price with quantity and whether they are tickets or retails
    const groupedPSSList = groupedPSS(
      BookingModule.selectedBooking ? BookingModule.selectedBooking.pssList : []
    );
    // Filter out cancelled, confirmed and abandoned pss
    // because we have already checked availability for confirmed (sold) at the moment when we sold it
    const skippedStatuses = [
      BookingStatus.Canceled,
      BookingStatus.Confirmed,
      BookingStatus.Abandoned,
    ];
    const pendingPSSList = groupedPSSList.filter(
      (pss) => !skippedStatuses.includes(pss.bookingStatus as BookingStatus)
    );
    // For each pending item lets check if the availability in local db covers the item.quantity
    for (const grpdPSS of pendingPSSList) {
      if (grpdPSS.pssType === PSSType[PSSType.Ticket]) {
        // compare to availabilities that are in timeslots stored in local db
        const timeSlotAvailability = this.selectedBookingAvailabilityInfo.find(
          (info) => info.timeslotId === grpdPSS.timeSlotId
        );
        const dbAvailability = timeSlotAvailability?.dbAvailability || null;
        const sharedStocksAllowOverbookings =
          timeSlotAvailability?.stockIds &&
          timeSlotAvailability?.stockIds.includes(-1000);
        if (!dbAvailability && !sharedStocksAllowOverbookings) return false;
        if (dbAvailability && !sharedStocksAllowOverbookings) {
          // Sum in stock units so a privatization is not counted as a
          // single unit (L3-7946)
          const ownUnits = Math.max(1, grpdPSS.stockUnits || 1);
          let totalUnits = grpdPSS.quantity * ownUnits;
          if (
            grpdPSS.stockIds?.length &&
            grpdPSS.startDateTime &&
            grpdPSS.endDateTime
          ) {
            for (const other of pendingPSSList) {
              if (
                other === grpdPSS ||
                other.pssType !== PSSType[PSSType.Ticket]
              )
                continue;
              if (!other.stockIds?.some((id) => grpdPSS.stockIds!.includes(id)))
                continue;
              if (
                other.startDateTime &&
                other.endDateTime &&
                dayjs(other.startDateTime).isBefore(grpdPSS.endDateTime) &&
                dayjs(other.endDateTime).isAfter(grpdPSS.startDateTime)
              ) {
                totalUnits +=
                  other.quantity * Math.max(1, other.stockUnits || 1);
              }
            }
          }
          // dbAvailability is in tickets of this category
          if (dbAvailability * ownUnits < totalUnits) return false;
        }
      }
      if (grpdPSS.pssType === PSSType[PSSType.Retail]) {
        // compare to availabilities that are in prices stored in local db
        const availability = this.selectedBookingAvailabilityInfo.find(
          (info) => info.priceId === grpdPSS.priceId
        );
        if (
          availability?.dbAvailability &&
          !availability?.sellOnSoldOut &&
          availability.dbAvailability < grpdPSS.quantity
        )
          return false;
      }
    }
    return true;
  }

  /**
   * Loop thru a grouped pssList and return an array of availability info for each booked retailPrice/ticketTimeslot
   */
  get selectedBookingAvailabilityInfo(): IAvailabilityInfo[] {
    return this.bookingAvailabilityInfo;
  }

  /**
   * Returns total amound of the booking
   */
  get bookingTotal() {
    const booking = this.selectedBooking;
    let total = 0;

    if (!booking) {
      return total;
    }

    for (const pss of booking.pssList) {
      total += getPSSValue(pss);
    }

    return total;
  }

  /**
   * A Free booking is the one with all (pss.priceValue = 0 and no pss._discountValue) or pss.bookingStatus = abandoned
   */
  get isFreeBooking() {
    const pssList = this.selectedBooking?.pssList;
    if (!pssList) {
      return false;
    }

    for (const pss of pssList) {
      if (
        (getPSSValue(pss) === 0 && pss._discountValue === 0) ||
        pss.bookingStatus === BookingStatus.Abandoned
      ) {
        continue;
      } else {
        return false;
      }
    }

    return true;
  }

  /**
   * A Full discount booking is the one with:
   * 1- all pss.priceValue = 0 and pss._discountValue > 0 and pss.paymentStatus !== NotStarted
   * OR
   * 2- all pss.priceValue === pss._discountValue !== 0 and pss.paymentStatus === NotStarted
   */
  get isFullDiscountBooking() {
    const pssList = this.selectedBooking?.pssList;
    if (!pssList) {
      return false;
    }

    for (const pss of pssList) {
      if (
        (getPSSValue(pss) === 0 &&
          pss._discountValue === 0 &&
          pss.paymentStatus !== PaymentStatus.NotStarted) ||
        (getPSSValue(pss) === pss._discountValue &&
          getPSSValue(pss) !== 0 &&
          pss.paymentStatus === PaymentStatus.NotStarted)
      ) {
        continue;
      } else {
        return false;
      }
    }

    return true;
  }

  /**
   * A booking that has free tickets and a full discount is a booking:
   * 1- 0 amount
   * 2- Not a full discount only booking
   * 3- Not a free tickets only booking
   */
  get bookingHasFreeAndFullDiscount() {
    if (
      this.discountTotal === this.pssSum &&
      !this.isFreeBooking &&
      !this.isFullDiscountBooking
    ) {
      return true;
    }
    return false;
  }

  /**
   * Returns total amound of the discount
   */
  get discountTotal() {
    let sum = 0;
    BookingModule.selectedBookingDiscountTrack?.pssDiscounts.forEach(
      (pss) => (sum = sum + (pss.pssOriginalPriceValue - pss.pssNewPriceValue))
    );
    return sum;
  }

  /**
   * Returns (sum of pss values if paymentStatus not completed OR sum of pss values and _discountValues if paymentStatus completed(in case of cash)
   * or has started (in case of cards))
   *
   */
  get pssSum() {
    let amount = 0;
    const pssList = BookingModule.selectedBooking?.pssList;

    if (!pssList) {
      return amount;
    }

    for (let i = 0; i < pssList.length; i++) {
      amount +=
        pssList[i].paymentStatus === PaymentStatus.Completed ||
        pssList[i].paymentStatus === PaymentStatus.Started
          ? getPSSValue(pssList[i]) + (pssList[i]._discountValue || 0)
          : getPSSValue(pssList[i]);
    }
    return amount;
  }

  /**
   * Returns paid amound of the booking
   */
  get totalPaid() {
    const booking = this.selectedBooking;
    let total = 0;

    if (!booking) {
      return total;
    }
    const completedAccountings = booking.accountings.filter(
      (acc) => acc.state === 5
    );
    for (const acc of completedAccountings) {
      for (const entry of acc.accountingEntriesList) {
        total += entry.value;
      }
    }

    return total;
  }

  /**
   * returns what was paid, and it considers the cash bills provided even if it was bigger than its accounting
   */
  public get amountPaid() {
    let oldCashSum = 0;
    // only consider completed accountings (state 5) that were paid in cash and come from backend (they have an id)
    const oldCashAccountings =
      BookingModule.selectedBooking?.accountings.filter(
        (acc) =>
          acc.state === 5 &&
          acc.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
          acc.id
      );
    if (oldCashAccountings && oldCashAccountings.length) {
      const aeValues = oldCashAccountings
        ?.map((acc) => acc.accountingEntriesList)
        .flat()
        .map((ae) => ae.value);
      if (aeValues && aeValues.length) {
        oldCashSum = sumArray(aeValues);
      }
    }
    return (
      BookingModule.totalNonCashPaidAmount +
      oldCashSum +
      BookingModule.sumOfProvidedCashBills
    );
  }

  public get codesPaymentSum() {
    // Returns the sum of all accEntries.value of all payments (done using codes) that are completed
    // promocode is not considered because it is a discount
    const completedCodesPaymentAcc =
      BookingModule.selectedBooking?.accountings.filter(
        (acc) =>
          acc.state === AccountingState.Completed &&
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT &&
          (acc.paymentMethod === PaymentMethods.POS_GIFT_CARD ||
            acc.paymentMethod === PaymentMethods.POS_MEMBERSHIP)
      );
    if (!completedCodesPaymentAcc || !completedCodesPaymentAcc.length) {
      return 0;
    }
    const accEntriesValues = completedCodesPaymentAcc
      .map((acc) => acc.accountingEntriesList)
      .flat()
      .map((ae) => ae.value);
    if (accEntriesValues && accEntriesValues.length) {
      return sumArray(accEntriesValues);
    } else {
      return 0;
    }
  }

  /**
   * Returns the current amount of the booking that is going to be paid
   */
  get currentAmount(): number {
    return this.bookingsAmountsTrack[this.selectedBookingUUID]
      ? this.bookingsAmountsTrack[this.selectedBookingUUID].currentAmount
      : 0;
  }

  /**
   * Returns the confirmed amounts of the booking that is going to be paid
   */
  get confirmedAmounts() {
    return this.bookingsAmountsTrack[this.selectedBookingUUID]
      ? this.bookingsAmountsTrack[this.selectedBookingUUID].confirmedAmounts
      : [];
  }

  /**
   * Returns the confirmed amounts and change given back by currency only of the booking that is going to be paid in cash
   * the change is not deducted from total amount for main currency
   * for secondary currencies we take the sum of cash bills
   */
  get orderCashAndChangeAmount() {
    if (this.isFreeBooking || this.isFullDiscountBooking) {
      return {};
    }
    // Select accountings of type payment/refund and performed with CASH, and not coming from backend (accounting having no id)
    const cashAccountings = this.selectedBooking?.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Completed &&
        (acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT ||
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND) &&
        acc.paymentMethod === PaymentMethods.METHOD_POS_CASH &&
        !acc.id &&
        !acc.addedToCashFloat
    );
    if (cashAccountings && cashAccountings.length) {
      const cashAmounts: CashAmounts = {};
      const changeAmounts: Partial<
        Record<Currencies, { value: number; exchangeRate: number }>
      > = {};
      const groupCurrency = POSConfigModule.getGroupCurrency as Currencies;
      cashAccountings.forEach((accounting) => {
        if (
          !accounting.paymentCurrency ||
          accounting.paymentCurrency === groupCurrency
        ) {
          // main currency, we sum the accounting entries values
          let cashAmountsWithoutChange = 0;
          accounting.accountingEntriesList.forEach(
            (ae) => (cashAmountsWithoutChange += ae.value)
          );

          if (!cashAmounts[groupCurrency]) {
            cashAmounts[groupCurrency] = cashAmountsWithoutChange;
          } else {
            cashAmounts[groupCurrency]! += cashAmountsWithoutChange;
          }
        } else {
          // secondary currencies, we sum the provided cash bills because no change is given back with these currencies
          // paymentCurrency cant be undefined because we checked in the previous condition
          const paymentCurrency = accounting.paymentCurrency!;
          if (!cashAmounts[paymentCurrency]) {
            cashAmounts[paymentCurrency] = accounting.providedCashBill || 0;
          } else {
            cashAmounts[paymentCurrency]! += accounting.providedCashBill || 0;
          }
        }
        const paymentCurrency = accounting.paymentCurrency;
        const paymentAndChange = accounting.paymentAndChange;
        if (paymentCurrency && paymentAndChange) {
          // keep track of change given back
          if (!changeAmounts[paymentCurrency]) {
            changeAmounts[paymentCurrency] = {
              value: paymentAndChange.changeValue,
              exchangeRate: accounting.exchangeRate || 1,
            };
          } else {
            changeAmounts[paymentCurrency]!.value +=
              paymentAndChange.changeValue;
          }
        }
        // consider this accounting added to cashfloat amounts
        // this avoids adding values multiple times in case of editing orders
        accounting.addedToCashFloat = true;
      });
      return {
        cashAmounts,
        changeAmounts,
      };
    }
    return {};
  }

  /**
   * Returns sum of the confirmed amounts of the booking that is going to be paid
   */
  get confirmedAmountsTotal(): number {
    if (!this.bookingsAmountsTrack[this.selectedBookingUUID]) {
      return 0;
    }
    const confirmedAmounts = this.confirmedAmounts.map(
      (c) => c.confirmedAmount
    );
    let total = 0;
    for (let i = 0; i < confirmedAmounts.length; i++) {
      total = total + confirmedAmounts[i];
    }
    return total;
  }

  /**
   * Returns sum of the cash bills provided during the payment
   * in split payment we can have multiple cash payments
   */
  get sumOfProvidedCashBills(): number {
    const booking = this.selectedBooking;
    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }
    // only consider accountings that do not contain an id, meaning they were not pulled from backend
    // this avoids duplicating cash amounts when calculating the amount paid of a given booking
    const completedAccounting = booking.accountings
      .filter((acc) => acc.state == AccountingState.Completed && !acc.id)
      .map((acc) => {
        return {
          currency: acc.currency,
          providedCashBill: acc.providedCashBill,
          exchangeRate: acc.exchangeRate,
        };
      });
    let total = 0;
    for (let i = 0; i < completedAccounting.length; i++) {
      const currency = completedAccounting[i].currency;
      const cashBillValue = completedAccounting[i].providedCashBill || 0;
      const reverseExchangeRate =
        1 / (completedAccounting[i].exchangeRate || 1);
      let convertedCashBillValue = Math.round(
        cashBillValue * reverseExchangeRate
      );
      if (currency === Currencies.CHF) {
        convertedCashBillValue = roundToNearestMultipleOf5(
          convertedCashBillValue
        );
      }
      total = total + convertedCashBillValue;
    }
    return total;
  }

  /**
   * Returns the total non cash paid amount of the booking
   * This sums up all non cash completed accountings of transaction type payment or refund
   */
  get totalNonCashPaidAmount(): number {
    if (!this.selectedBooking) {
      return 0;
    }
    // PS: cash roundings are excluded from this getter, they are reported on
    // their own line rather than counted as a non-cash payment.
    const completedAccountings = this.selectedBooking.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Completed &&
        acc.paymentMethod !== PaymentMethods.METHOD_POS_CASH &&
        acc.paymentMethod !== PaymentMethods.METHOD_CASH_ROUNDING
    );
    let total = 0;
    for (let i = 0; i < completedAccountings.length; i++) {
      const accounting = completedAccountings[i];
      if (
        accounting.transactionType ===
          TransactionTypes.TRANSACTION_TYPE_PAYMENT ||
        accounting.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND
      ) {
        for (let j = 0; j < accounting.accountingEntriesList.length; j++) {
          const entry = accounting.accountingEntriesList[j];
          total = total + entry.value;
        }
      }
    }
    return total;
  }

  /**
   * Total Belgian cash rounding booked against the selected booking, in cents.
   * Positive when the customer paid less than the exact total.
   */
  get roundingTotal(): number {
    if (!this.selectedBooking) {
      return 0;
    }
    const roundingAccountings = this.selectedBooking.accountings.filter(
      (acc) =>
        acc.state === AccountingState.Completed &&
        acc.paymentMethod === PaymentMethods.METHOD_CASH_ROUNDING &&
        (acc.transactionType === TransactionTypes.TRANSACTION_TYPE_PAYMENT ||
          acc.transactionType === TransactionTypes.TRANSACTION_TYPE_REFUND)
    );
    return sumArray(
      roundingAccountings.flatMap((acc) =>
        acc.accountingEntriesList.map((entry) => entry.value)
      )
    );
  }

  // /**
  //  * Returns sum of the confirmed amounts of the booking that is still not considered as a completed payment (completed accounting)
  //  */
  // get confirmedAmountWithoutCompletedAccounting(): number {
  //   if (!this.bookingsAmountsTrack[this.selectedBookingUUID]) {
  //     return 0;
  //   }
  //   const notPaidAccountings = this.selectedBooking?.accountings.filter(
  //     (acc) => acc.state !== AccountingState.Completed
  //   );
  //   const confirmedAmountsWithNotPaidAccountings = this.confirmedAmounts
  //     .filter((c) =>
  //       notPaidAccountings
  //         ?.map((acc) => acc.uuid)
  //         .includes(c.accountingInfo.accountingUUId)
  //     )
  //     .map((c) => c.confirmedAmount);
  //   let total = 0;
  //   for (let i = 0; i < confirmedAmountsWithNotPaidAccountings.length; i++) {
  //     total = total + confirmedAmountsWithNotPaidAccountings[i];
  //   }
  //   return total;
  // }

  /**
   * Returns selected payment method of the current booking
   */
  get selectedBookingPaymentMethod(): PaymentTool | null {
    if (!this.selectedBooking) {
      return null;
    }

    const amountTrack = this.bookingsAmountsTrack[this.selectedBookingUUID];

    if (!amountTrack) {
      return null;
    }

    return amountTrack.paymentTool;
  }

  get selectedBookingPaymentMethodId(): string | null {
    if (!this.selectedBooking) {
      return null;
    }

    const amountTrack = this.bookingsAmountsTrack[this.selectedBookingUUID];

    if (!amountTrack || !amountTrack.paymentToolId) {
      return null;
    }

    return amountTrack.paymentToolId;
  }

  get selectedBookingPayment(): IBookingPayment | null {
    if (!this.selectedBooking) {
      return null;
    }

    const bookingPayment = this.bookingsAmountsTrack[this.selectedBookingUUID];

    if (!bookingPayment) {
      return null;
    }

    return bookingPayment;
  }

  get selectedBookingDiscountTrack(): IBookingDiscount | null {
    if (!this.selectedBooking) {
      return null;
    }

    const discountTrack = this.bookingsDiscountsTrack[this.selectedBookingUUID];

    if (!discountTrack) {
      return null;
    }

    return discountTrack;
  }

  // return current booking's adyen accountings and their adyen response status
  get selectedBookingAdyenReponseStatusTrack() {
    if (!this.selectedBooking) {
      return {};
    }
    const adyenResponseStatusTrack =
      this.adyenResponseStatusTrack[this.selectedBookingUUID];
    if (!adyenResponseStatusTrack) {
      return {};
    }
    return adyenResponseStatusTrack;
  }

  get bookingVisibleTabs() {
    return bookings.value.length > maxBookingTabsToShow
      ? bookings.value.slice(bookings.value.length - maxBookingTabsToShow)
      : bookings.value;
  }

  get bookingHiddenTabs() {
    return bookings.value.length > maxBookingTabsToShow
      ? bookings.value.slice(0, bookings.value.length - maxBookingTabsToShow)
      : [];
  }

  /**
   * Checks if the current order session has no accountingEntries linked to it
   * Absence of accountingEntries linked to an order session is a sign of no pss creation nor pss cancellation
   */
  get isCurrentOrderSessionEmpty() {
    const currentOrderSessionId = this.currentOrderSessionId;
    if (!currentOrderSessionId) {
      return true;
    }

    if (!this.selectedBooking) {
      return true;
    }

    if (this.selectedBooking.accountings.length === 0) {
      return true;
    }

    const accountingEntries = this.selectedBooking.accountings
      .map((acc) => acc.accountingEntriesList)
      .flat();
    if (accountingEntries.length === 0) {
      return true;
    }

    const orderSessionsIds: string[] = accountingEntries.map(
      (accEntry) => accEntry.orderSessionId
    );
    if (orderSessionsIds.length === 0) {
      return true;
    }

    return !orderSessionsIds.includes(currentOrderSessionId);
  }

  @Mutation
  linkWaiver(data: { bookingUUID: string; waiver: ISignedWaiver }) {
    const { bookingUUID, waiver } = data;
    if (!this.bookingsWaiversTrack[bookingUUID]) {
      this.bookingsWaiversTrack[bookingUUID] = { linkedWaivers: [] };
    }
    const alreadyLinked = this.bookingsWaiversTrack[
      bookingUUID
    ].linkedWaivers.some((w) => w.waiverId === waiver.waiverId);
    if (!alreadyLinked) {
      this.bookingsWaiversTrack[bookingUUID].linkedWaivers.push(waiver);
    }
  }

  @Mutation
  unlinkWaiver(data: { bookingUUID: string; waiverId: string }) {
    const { bookingUUID, waiverId } = data;
    if (!this.bookingsWaiversTrack[bookingUUID]) return;
    this.bookingsWaiversTrack[bookingUUID].linkedWaivers =
      this.bookingsWaiversTrack[bookingUUID].linkedWaivers.filter(
        (w) => w.waiverId !== waiverId
      );
  }

  @Mutation
  clearBookingWaivers(bookingUUID: string) {
    if (this.bookingsWaiversTrack[bookingUUID]) {
      delete this.bookingsWaiversTrack[bookingUUID];
    }
  }

  // @Mutation
  // updatedIsLoading(value: boolean) {
  //   this.isLoading = value;
  // }
  @Mutation
  setTabAnimation(isAnimated: boolean) {
    this.isTabAnimated = isAnimated;
  }

  @Mutation
  addPromocode(data: {
    promocode: IPromocode;
    bookingUuid: string;
    token: string;
    pssUuids: string[];
    offerIds: number[];
    membershipId?: number;
  }) {
    // Commenting beloc piece of code so that we can have more than one promocode in the state
    // // if the same booking has already a promocode item here, lets remove it
    // const oldIndex = this.promocodes.findIndex(
    //   (el) => el.bookingUuid === data.bookingUuid
    // );
    // if (oldIndex >= 0) {
    //   this.promocodes.splice(oldIndex, 1);
    // }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line prefer-const
    let item: IPromocodeTrack = {
      id: String(data.promocode.promoCodeId) + ":" + data.bookingUuid,
      promocode: data.promocode,
      bookingUuid: data.bookingUuid,
      createdAt: dayjs().toISOString(),
      token: data.token,
      isValidAllDays: data.promocode.isValidAllDays,
      validDays: data.promocode.validDays,
      pssUuids: data.pssUuids,
      offerIds: data.offerIds,
      membershipId: data.membershipId,
    };
    this.promocodes.push(item);
  }

  @Mutation
  removePromocodes(bookingUuid: string) {
    // remove all promocodes related to this booking
    this.promocodes = this.promocodes.filter(
      (el) => el.bookingUuid !== bookingUuid
    );
  }

  @Mutation
  removePromocode(data: { bookingUuid: string; promocodeUuid: string }) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // eslint-disable-next-line prefer-const
    const index = this.promocodes.findIndex(
      (el) =>
        el.bookingUuid === data.bookingUuid &&
        el.promocode.code === data.promocodeUuid
    );
    if (index >= 0) {
      this.promocodes.splice(index, 1);
    }
  }

  @Mutation
  removeOfferPromocodes(bookingUuid: string) {
    this.promocodes = this.promocodes.filter(
      (p) =>
        p.bookingUuid !== bookingUuid ||
        (p.bookingUuid === bookingUuid && p.offerIds.length === 0)
    );
  }

  @Mutation
  SET_CALC_VALUE(payload: string) {
    this.calculatorValue = payload;
  }

  @Mutation
  restore(bookingFields: Partial<IBookingState>) {
    const {
      bookings,
      bookingList,
      bookingsSteps,
      selectedBookingUUID,
      bookingsAmountsTrack,
      bookingsDiscountsTrack,
      promocodes,
      bookingsPendingSynchronization,
      adyenResponseStatusTrack,
      currentOrderSessionId,
      recentTabNames,
      currentBookingCount,
      refundListeners,
      bookingsWaiversTrack,
      bookingChanges,
    } = bookingFields;

    // keep a record of pending accountings that were marked as failed after a reload
    // it will be used to update adyenResponseStatusTrack when restoring it from local storage
    const adyenResponseStatusTrackUpdates: IAdyenResponseStatusTrack = {};

    if (bookings) {
      // after a reload, check and mark locally created pending adyen accountings as failed when restoring bookings
      // this is in order to prevent POS from confirming these accountings without a pspReference upon booking completion
      for (const uuid in bookings) {
        const booking = bookings[uuid];
        booking.accountings.forEach((accounting) => {
          if (
            accounting.paymentMethod === PaymentMethods.METHOD_POS_ADYEN &&
            accounting.state === AccountingState.Pending &&
            !accounting.id
          ) {
            LogService.info(
              `Pending adyen accounting found after a reload, marking it as failed. booking: ${booking.uuid}, accounting: ${accounting.uuid}`
            );
            accounting.state = AccountingState.Failed;
            if (booking.uuid && accounting.uuid) {
              if (!adyenResponseStatusTrackUpdates[booking.uuid]) {
                // initialize if not already
                adyenResponseStatusTrackUpdates[booking.uuid] = {};
              }
              adyenResponseStatusTrackUpdates[booking.uuid][accounting.uuid] =
                AdyenResponseStatus.Aborted;
            }
          }
        });
      }
      this.bookings = bookings;
    }
    if (bookingList) {
      this.bookingList = bookingList;
    }
    if (bookingsSteps) {
      this.bookingsSteps = bookingsSteps;
    }
    if (selectedBookingUUID) {
      this.selectedBookingUUID = selectedBookingUUID;
    }
    if (bookingsAmountsTrack) {
      this.bookingsAmountsTrack = bookingsAmountsTrack;
    }

    if (bookingsDiscountsTrack) {
      this.bookingsDiscountsTrack = bookingsDiscountsTrack;
    }

    if (promocodes) {
      this.promocodes = promocodes;
    }

    if (bookingsPendingSynchronization) {
      if (Array.isArray(bookingsPendingSynchronization)) {
        // migration after changing the type from string[] to IBookingsPendingSync
        LogService.info("WARNING: unsynced bookings array not migrated", {
          bookingsPendingSynchronization,
        });
        this.bookingsPendingSynchronization = {};
      } else {
        this.bookingsPendingSynchronization = bookingsPendingSynchronization;
      }
    }

    if (adyenResponseStatusTrack) {
      // we update the adyen response status track according to the object created before restoring bookings
      for (const bookingUuid in adyenResponseStatusTrackUpdates) {
        const accountings = adyenResponseStatusTrackUpdates[bookingUuid];
        for (const accountingUuid in accountings) {
          const response = accountings[accountingUuid];
          // safety check in case of undefined properties
          if (
            adyenResponseStatusTrack[bookingUuid] &&
            adyenResponseStatusTrack[bookingUuid][accountingUuid]
          )
            adyenResponseStatusTrack[bookingUuid][accountingUuid] = response;
        }
      }
      this.adyenResponseStatusTrack = adyenResponseStatusTrack;
    }

    if (currentOrderSessionId) {
      this.currentOrderSessionId = currentOrderSessionId;
    }

    if (recentTabNames) {
      this.recentTabNames = recentTabNames;
    }

    if (currentBookingCount) {
      this.currentBookingCount = currentBookingCount;
    }

    if (refundListeners) {
      this.refundListeners = refundListeners;
      // restart refund listeners
      for (const [bookingUuid, accountingUuids] of Object.entries(
        this.refundListeners
      )) {
        for (const accountingUuid of accountingUuids) {
          startRefundListener(bookingUuid, accountingUuid);
        }
      }
    }

    if (bookingsWaiversTrack) {
      this.bookingsWaiversTrack = bookingsWaiversTrack;
    }

    if (bookingChanges) {
      this.bookingChanges = bookingChanges;
    }
  }

  @Mutation
  setBookingStep(payload: { bookingUUID: string; step: BookingStep }) {
    const { bookingUUID, step } = payload;

    const booking = this.bookings[bookingUUID];
    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    LogService.info(
      `BookingModule: Setting step '${step}' on booking ${bookingUUID}`
    );
    this.bookings[bookingUUID].step = step;
  }

  @Mutation
  removeBookingStep(bookingUUID: string) {
    if (this.bookingsSteps[bookingUUID]) {
      delete this.bookingsSteps[bookingUUID];
    }
  }

  @Mutation
  addBooking(booking: IProductSlotBooking) {
    const uuid = booking.uuid as string;
    const reference = booking.reference as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookings[uuid] = booking;
    this.bookingList.push({ uuid: uuid, reference: reference });
  }

  @Mutation
  removeBookingFromList(booking: IProductSlotBooking) {
    const uuid = booking.uuid;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    delete this.bookings[uuid];
    // pull(this.bookingList, uuid);
    this.bookingList = this.bookingList.filter((el) => el.uuid !== uuid);
  }

  @Mutation
  selectBookingMutation(payload: {
    bookingUUID: string;
    currentOrderSessionId: string;
    suspendBookingUUID?: string;
  }) {
    const { bookingUUID, currentOrderSessionId, suspendBookingUUID } = payload;

    if (suspendBookingUUID) {
      this.bookings[suspendBookingUUID].isSuspended = true;
    }

    this.selectedBookingUUID = bookingUUID;
    this.currentOrderSessionId = currentOrderSessionId;
  }

  @Mutation
  storeBookingInPendingSyncBookings(data: {
    bookingUUID: string;
    type: SyncType;
  }) {
    const { bookingUUID, type } = data;

    if (!bookingUUID) {
      throw new Error(PSBErrors.NoSelectedBookingInModule);
    }

    // Initialize array if it doesn't exist
    this.bookingsPendingSynchronization[bookingUUID] ??= [];

    // Add type only if it doesn't already exist
    if (!this.bookingsPendingSynchronization[bookingUUID].includes(type)) {
      this.bookingsPendingSynchronization[bookingUUID].push(type);
    }
  }

  @Mutation
  removeSyncedBookingFromPendingSyncBookings(data: {
    synchedBooking: string[];
    typeToDelete?: SyncType;
  }) {
    const { synchedBooking, typeToDelete } = data;
    synchedBooking.forEach((uuid) => {
      if (!this.bookingsPendingSynchronization[uuid]) return; // Skip if UUID doesn't exist

      // Filter out only typeToDelete from the array
      this.bookingsPendingSynchronization[uuid] =
        this.bookingsPendingSynchronization[uuid].filter(
          (type) => type !== typeToDelete
        );

      // If no types are left, remove the UUID entry
      if (this.bookingsPendingSynchronization[uuid].length === 0) {
        delete this.bookingsPendingSynchronization[uuid];
      }
    });
  }

  @Mutation
  clearAllPendingToSyncBookings(typeToDelete?: SyncType) {
    if (typeToDelete === undefined) {
      // Reset the whole record if a type is not provided
      this.bookingsPendingSynchronization = {};
      return;
    }
    this.bookingsPendingSynchronization = Object.fromEntries(
      Object.entries(this.bookingsPendingSynchronization)
        .map(([key, types]) => [
          key,
          types.filter((type) => type !== typeToDelete),
        ])
        .filter(([_, types]) => types.length > 0) // Remove keys with empty arrays
    );
  }

  @Mutation
  async addPSS(data: { bookingUUID: string; pssList: IProductSlotState[] }) {
    const { bookingUUID, pssList } = data;
    const booking = this.bookings[bookingUUID];

    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    // booking.pssList = (booking.pssList || []).concat(pssList);
    // concat first tests if one of its argument is a real array. this test fails when there is a proxy for array. Use the below method instead.
    pssList.forEach((pss) => booking.pssList.push(pss));
    LogService.info(
      `Current pssList updated, booking: ${bookingUUID}`,
      pssList
    );

    // apply offers after pss list is updated
    await applyOffersToBooking(cloneDeep(booking));
  }

  @Mutation
  removePSS(data: { bookingUUID: string; criteria: IRemovePSS }) {
    const { bookingUUID, criteria } = data;
    const booking = this.bookings[bookingUUID];
    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    removePSSFromPSB(criteria, booking);
  }

  @Mutation
  setCustomerData(data: IBookingCustomerInfo) {
    LogService.info(`Assigning customer to booking: ${data.bookingUuid}`, {
      data,
    });
    const id = data.id;
    const uuid = data.bookingUuid;
    const email = data.email;
    const lastName = data.lastName;
    const firstName = data.firstName;
    this.bookings[uuid].customerId = id;
    this.bookings[uuid].email = email;
    this.bookings[uuid].lastName = lastName;
    this.bookings[uuid].firstName = firstName;
  }

  @Mutation
  updateBooking(booking: IProductSlotBooking) {
    const uuid = booking.uuid as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookings[uuid] = booking;
  }

  @Mutation
  addPaymentTracking(data: { BookingUUID: string; track: IBookingPayment }) {
    const { BookingUUID, track } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsAmountsTrack[uuid] = track;
  }

  @Mutation
  removeAmountTracking(bookingUUID: string) {
    if (this.bookingsAmountsTrack[bookingUUID]) {
      delete this.bookingsAmountsTrack[bookingUUID];
    }
  }

  @Mutation
  setPaymentCurrency(data: { BookingUUID: string; currency: Currencies }) {
    const { BookingUUID, currency } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookingsAmountsTrack[uuid].currency = currency;
  }

  @Mutation
  setPaymentTool(data: {
    BookingUUID: string;
    paymentTool: PaymentTool;
    paymentToolId: string;
  }) {
    const { BookingUUID, paymentTool, paymentToolId } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookingsAmountsTrack[uuid].paymentTool = paymentTool;
    this.bookingsAmountsTrack[uuid].paymentToolId = paymentToolId;
  }

  @Mutation
  setPaymentCard(data: { BookingUUID: string; paymentCard: PaymentCard }) {
    const { BookingUUID, paymentCard } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookingsAmountsTrack[uuid].paymentCard = paymentCard;
  }

  @Mutation
  addCurrentAmount(data: { BookingUUID: string; amount: number }) {
    const { BookingUUID, amount } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookingsAmountsTrack[uuid].currentAmount = amount;
  }

  @Mutation
  updateCurrentAmount(data: { BookingUUID: string; amount: number }) {
    const { BookingUUID, amount } = data;

    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsAmountsTrack[uuid].currentAmount = amount;
  }

  @Mutation
  confirmCurrentAmount(data: {
    BookingUUID: string;
    accountingInfo: IAccountingInfo;
  }) {
    // Takes the current amount and push it to confirmed amounts array along with accountingInfo

    const { BookingUUID, accountingInfo } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    const confirmedAmount: IConfirmedAmount = {
      id: generateID(),
      confirmedAmount: this.bookingsAmountsTrack[uuid].currentAmount,
      accountingInfo: accountingInfo,
    };

    this.bookingsAmountsTrack[uuid].confirmedAmounts.push(confirmedAmount);
    this.bookingsAmountsTrack[uuid].currentAmount = 0;
  }

  @Mutation
  updateConfirmedAmounts(data: {
    bookingUUID: string;
    confirmedAmounts: IConfirmedAmount[];
  }) {
    const { bookingUUID, confirmedAmounts } = data;
    const amountTrack = this.bookingsAmountsTrack[bookingUUID];

    if (!amountTrack) {
      throw new Error(`Amount track not found for uuid ${bookingUUID}`);
    }

    amountTrack.confirmedAmounts = confirmedAmounts;
  }

  @Mutation
  setSplitPaymentAmount(data: {
    BookingUUID: string;
    splitAmount: number;
    splitAmountProvided?: number;
  }) {
    const { BookingUUID, splitAmount } = data;
    const splitAmountProvided = data.splitAmountProvided || splitAmount;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    this.bookingsAmountsTrack[uuid].splitPaymentAmount = splitAmount;

    this.bookingsAmountsTrack[uuid].splitAmountProvided = splitAmountProvided;
  }

  @Mutation
  removeSplitPaymentAmount(BookingUUID: string) {
    if (!BookingUUID) {
      throw new Error(PSBErrors.HasNoUUID);
    }

    if (this.bookingsAmountsTrack[BookingUUID].splitPaymentAmount) {
      delete this.bookingsAmountsTrack[BookingUUID].splitPaymentAmount;
    }
  }

  @Mutation
  updateBookingAccountings(data: {
    bookingUUID: string;
    accountings: IAccounting[];
  }) {
    const { bookingUUID, accountings } = data;
    const booking = this.bookings[bookingUUID];

    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule + ` - uuid = ${bookingUUID}`);
    }

    booking.accountings = accountings;
  }

  @Mutation
  deleteConfirmedAmount(id: string) {
    const uuid = this.selectedBookingUUID;
    const index = this.bookingsAmountsTrack[uuid].confirmedAmounts.findIndex(
      (c) => c.id === id
    );
    if (index > -1) {
      this.bookingsAmountsTrack[uuid].confirmedAmounts.splice(index, 1);
    }
  }

  @Mutation
  emptyConfirmedAmountsArray(bookingUUID: string) {
    this.bookingsAmountsTrack[bookingUUID].confirmedAmounts = [];
  }

  @Mutation
  addDiscountTracking(data: { BookingUUID: string; track: IBookingDiscount }) {
    const { BookingUUID, track } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsDiscountsTrack[uuid] = track;
  }

  @Mutation
  removeDiscountTracking(bookingUUID: string) {
    if (this.bookingsDiscountsTrack[bookingUUID]) {
      delete this.bookingsDiscountsTrack[bookingUUID];
    }
  }

  @Mutation
  updateDiscountType(data: { BookingUUID: string; type: DiscountType }) {
    const { BookingUUID, type } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsDiscountsTrack[uuid].type = type;
  }

  @Mutation
  updateDiscountValue(data: { BookingUUID: string; value: number }) {
    const { BookingUUID, value } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsDiscountsTrack[uuid].value = value;
  }

  @Mutation
  updateDiscountCoverage(data: { BookingUUID: string; isOnAllOrder: boolean }) {
    const { BookingUUID, isOnAllOrder } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsDiscountsTrack[uuid].isOnAllOrder = isOnAllOrder;
  }

  @Mutation
  updateDiscountPrices(data: { BookingUUID: string; pricesIds: number[] }) {
    const { BookingUUID, pricesIds } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsDiscountsTrack[uuid].pricesIds = pricesIds;
  }

  @Mutation
  updatePSSDiscount(data: {
    BookingUUID: string;
    pssDiscounts: IPSSDiscount[];
  }) {
    const { BookingUUID, pssDiscounts } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsDiscountsTrack[uuid].pssDiscounts = pssDiscounts;
  }

  @Mutation
  updatePSSPromocodeDiscount(
    pssPromocodeDiscounts: {
      pssUuid: string;
      pssPromocodeDiscountValue: number;
      promoCodeId: number | null;
    }[]
  ) {
    const booking = this.bookings[this.selectedBookingUUID];

    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    const pssList = cloneDeep(booking.pssList);

    for (const pss of pssList) {
      const discount = pssPromocodeDiscounts.find(
        (it) => it.pssUuid === pss.uuid
      );
      if (discount) {
        pss.promocodeDiscountValue = discount.pssPromocodeDiscountValue;
        pss.promoCodeId = discount?.promoCodeId;
      } else {
        pss.promocodeDiscountValue = 0;
        pss.promoCodeId = null;
      }
    }

    booking.pssList = pssList;
  }

  @Mutation
  applyDiscount(data: { BookingUUID: string; pssDiscounts: IPSSDiscount[] }) {
    const { BookingUUID, pssDiscounts } = data;
    const uuid = BookingUUID as string;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    this.bookingsDiscountsTrack[uuid].isApplied = true;
    this.bookingsDiscountsTrack[uuid].pssDiscounts = pssDiscounts;
    LogService.info(`Discount was applied ${BookingUUID}`, {
      params: pssDiscounts,
    });
    // Lets calculate the total amount of the discount
    let originalPriceValueSum = 0;
    this.bookingsDiscountsTrack[uuid].pssDiscounts.forEach(
      (pss) =>
        (originalPriceValueSum =
          originalPriceValueSum + pss.pssOriginalPriceValue)
    );
    let newPriceValueSum = 0;
    this.bookingsDiscountsTrack[uuid].pssDiscounts.forEach(
      (pss) => (newPriceValueSum = newPriceValueSum + pss.pssNewPriceValue)
    );
    this.bookingsDiscountsTrack[uuid].discountTotalValue =
      originalPriceValueSum - newPriceValueSum;
  }

  @Mutation
  updateAvailabilityInfo(avInfo: IAvailabilityInfo[]) {
    // lets first empty the array
    this.bookingAvailabilityInfo = [];

    for (const item of avInfo) {
      this.bookingAvailabilityInfo.push(item);
    }
  }

  @Mutation
  setSelectedCashCurrency(currency: Currency | null) {
    this.selectedCashCurrency = currency;
  }

  @Mutation
  setAdyenResponseStatusTrack(data: {
    bookingUuid: string;
    accountingUuid: string;
    status: AdyenResponseStatus;
  }) {
    if (!this.adyenResponseStatusTrack[data.bookingUuid]) {
      this.adyenResponseStatusTrack[data.bookingUuid] = {};
    }
    this.adyenResponseStatusTrack[data.bookingUuid][data.accountingUuid] =
      data.status;
  }

  @Mutation
  removeAdyenResponseStatusTrack(bookingUuid: string) {
    if (this.adyenResponseStatusTrack[bookingUuid]) {
      delete this.adyenResponseStatusTrack[bookingUuid];
    }
  }

  @Mutation
  setCurrentOrderSessionId(orderSessionId: string) {
    this.currentOrderSessionId = orderSessionId;
  }

  @Mutation
  updateOrderSession(data: {
    bookingUuid: string;
    orderSessionId: string;
    newOrderSession: BaseOrderSession;
  }) {
    const { bookingUuid, orderSessionId, newOrderSession } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }

    const index = booking.orderSessions.findIndex(
      (orderSession) => orderSession.id === orderSessionId
    );

    if (index === -1) {
      LogService.info("Order session not found", { orderSessionId });
      return;
    }

    // Replace and update
    booking.orderSessions[index] = {
      ...newOrderSession,
      updatedAt: dayjs().toISOString(),
    };
  }

  @Mutation
  completeOrderSession(data: {
    bookingUuid: string;
    orderSessionId: string;
    state: OrderSessionState;
  }) {
    const { bookingUuid, orderSessionId, state } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }
    const orderSession = booking.orderSessions.find(
      (orderSession) => orderSession.id === orderSessionId
    );
    if (!orderSession) {
      LogService.info("Order session not found", { orderSessionId });
      return;
    }
    if (orderSession.state === state) {
      LogService.info("Order session already completed", { state });
    }
    const completionTime = dayjs().toISOString();
    orderSession.state = state;
    orderSession.completionDate = completionTime;
    orderSession.sessionEnd = completionTime;
    orderSession.updatedAt = completionTime;
  }

  @Mutation
  addOrderSessionToBooking(data: {
    orderSession: BaseOrderSession;
    bookingUuid: string;
  }) {
    const { orderSession, bookingUuid } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }
    booking.orderSessions.push(orderSession);
    this.currentOrderSessionId = orderSession.id;
  }

  @Mutation
  setOrderSessionHasOrderLines(data: {
    bookingUuid: string;
    orderSessionId: string;
    hasOrderLines: boolean;
  }) {
    const { bookingUuid, orderSessionId, hasOrderLines } = data;
    const booking = this.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found", { bookingUuid });
      return;
    }
    const orderSession = booking.orderSessions.find(
      (orderSession) => orderSession.id === orderSessionId
    );
    if (!orderSession) {
      LogService.info("Order session not found", { orderSessionId });
      return;
    }
    orderSession.hasOrderLines = hasOrderLines;
  }

  @Mutation
  setBookingTabName(name: string) {
    const booking = this.bookings[this.selectedBookingUUID];
    if (!booking) {
      return;
    }
    booking.tabName = name;
  }

  @Mutation
  addRecentTabName(name: string) {
    const maxTabNames = 4;
    // do nothing if the name already exists in the array
    if (this.recentTabNames.includes(name)) {
      return;
    }
    // follows a LIFO behavior
    this.recentTabNames.unshift(name);
    if (this.recentTabNames.length > maxTabNames) {
      this.recentTabNames = this.recentTabNames.slice(
        0,
        this.recentTabNames.length - 1
      );
    }
  }

  /**
   * Swaps the order of two bookings in the bookings list
   */
  @Mutation
  swapBookings(data: { uuid1: string; uuid2: string }) {
    const { uuid1, uuid2 } = data;
    const entries = Object.entries(this.bookings);
    const index1 = entries.findIndex(([key]) => key === uuid1);
    const index2 = entries.findIndex(([key]) => key === uuid2);

    if (index1 !== -1 && index2 !== -1) {
      // Swap the entries
      [entries[index1], entries[index2]] = [entries[index2], entries[index1]];

      this.bookings = Object.fromEntries(entries);
    } else {
      return;
    }
  }

  @Mutation
  increaseBookingCount() {
    this.currentBookingCount++;
  }

  @Mutation
  resetBookingCount() {
    this.currentBookingCount = 0;
  }

  // Clear all bookings from module
  @Mutation
  clearAllBookingsData() {
    // clear all bookings and their related data
    this.bookings = {};
    this.bookingList = [];
    this.bookingsSteps = {};
    this.bookingsAmountsTrack = {};
    this.bookingsDiscountsTrack = {};
    this.promocodes = [];
    this.adyenResponseStatusTrack = {};
    this.bookingsWaiversTrack = {};
  }

  @Mutation
  setPromocodeBeingChecked(value: boolean) {
    this.promocodeBeingChecked = value;
  }

  @Mutation
  addRefundListener(data: { bookingUuid: string; accountingUuid: string }) {
    const { bookingUuid, accountingUuid } = data;
    if (!this.refundListeners[bookingUuid]) {
      this.refundListeners[bookingUuid] = [];
    }
    if (!this.refundListeners[bookingUuid].includes(accountingUuid)) {
      this.refundListeners[bookingUuid].push(accountingUuid);
    }
  }

  @Mutation
  removeRefundListener(data: { bookingUuid: string; accountingUuid: string }) {
    const { bookingUuid, accountingUuid } = data;
    if (this.refundListeners[bookingUuid]) {
      this.refundListeners[bookingUuid] = this.refundListeners[
        bookingUuid
      ].filter((uuid) => uuid !== accountingUuid);
      if (this.refundListeners[bookingUuid].length === 0) {
        delete this.refundListeners[bookingUuid];
      }
    }
  }

  @Mutation
  clearBookingRefundListeners(bookingUuid: string) {
    if (this.refundListeners[bookingUuid]) {
      delete this.refundListeners[bookingUuid];
    }
  }

  @Mutation
  addBookingChanges(data: { bookingUUID: string; changes: BookingChange[] }) {
    const { bookingUUID, changes } = data;
    if (!this.bookingChanges[bookingUUID]) {
      this.bookingChanges[bookingUUID] = [];
    }
    this.bookingChanges[bookingUUID].push(...changes);
  }

  @Mutation
  increaseBookingChangeQuantityByIndex(data: {
    bookingUUID: string;
    changeIndex: number;
  }) {
    const { bookingUUID, changeIndex } = data;
    if (changeIndex !== undefined && changeIndex > -1) {
      // quantities are negative for removals, so "increasing" means decrementing
      this.bookingChanges[bookingUUID][changeIndex].quantity -= 1;
    }
  }

  @Mutation
  clearBookingChanges(bookingUUID: string) {
    delete this.bookingChanges[bookingUUID];
  }

  /**
   * Build availability infos array of the selected booking and mutate it in state
   */
  @Action({ rawError: true })
  public async updateBookingAvailabilityInfo() {
    const bookingAvailabilityInfo: IAvailabilityInfo[] = [];
    const groupedPSSList = groupedPSS(
      BookingModule.selectedBooking ? BookingModule.selectedBooking.pssList : []
    );

    // We only check availabilities of pending pss (not cancelled, confirmed, or abandoned)
    const skippedStatuses = [
      BookingStatus.Canceled,
      BookingStatus.Confirmed,
      BookingStatus.Abandoned,
    ];
    const pendingPSSList = groupedPSSList.filter(
      (pss) => !skippedStatuses.includes(pss.bookingStatus as BookingStatus)
    );

    // get timeslots from localDB only for pending tickets
    const timeslotIds = [
      ...new Set(
        pendingPSSList
          .filter(
            (item) =>
              item.pssType === PSSType[PSSType.Ticket] &&
              item.timeSlotId != null
          )
          .map((item) => item.timeSlotId as number)
      ),
    ];
    const timeslots = await getTimeslotsByIds(timeslotIds);

    // For each pending item lets check if the availability in local db covers the item.quantity
    for (const grpdPSS of pendingPSSList) {
      if (grpdPSS.pssType === PSSType[PSSType.Ticket] && grpdPSS.timeSlotId) {
        // get availabilities that are in timeslots stored in local db
        const dbAvailability =
          timeslots.find((ts) => ts.id === grpdPSS.timeSlotId)?.availability ||
          0;

        const timeslotAvailabilityInfo = {
          categoryId: grpdPSS.categoryId,
          categoryName: grpdPSS.categoryName,
          priceId: grpdPSS.priceId,
          priceName: grpdPSS.priceName,
          timeslotId: grpdPSS.timeSlotId,
          bookedQty: grpdPSS.quantity,
          dbAvailability: dbAvailability,
          pssIds: grpdPSS.pssIds,
          stockIds: grpdPSS.stockIds || [],
        };
        bookingAvailabilityInfo.push(timeslotAvailabilityInfo);
      } else if (grpdPSS.pssType === PSSType[PSSType.Retail]) {
        // get availabilities that are in prices stored in local db
        let dbAvailability = 0;
        const pcDB = await getProductCategoryByPriceId(grpdPSS.priceId);
        const price = pcDB?.prices.find((pr) => pr.id === grpdPSS.priceId);
        if (price) dbAvailability = price.quantity;
        const priceAvailabilityInfo: IAvailabilityInfo = {
          categoryId: grpdPSS.categoryId,
          categoryName: grpdPSS.categoryName,
          priceId: grpdPSS.priceId,
          priceName: grpdPSS.priceName,
          bookedQty: grpdPSS.quantity,
          dbAvailability: dbAvailability,
          pssIds: grpdPSS.pssIds,
          sellOnSoldOut: sellOnSoldOut(pcDB?.prices, pcDB?.type),
          stockIds: pcDB?.stockIds || [],
        };
        bookingAvailabilityInfo.push(priceAvailabilityInfo);
      }
    }
    this.updateAvailabilityInfo(bookingAvailabilityInfo);
    // this.context.commit("updateAvailabilityInfo", bookingAvailabilityInfo);
  }

  @Action({ rawError: true })
  public async completeSelectedBooking() {
    const booking = this.bookings[this.selectedBookingUUID];

    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    // Update a copied booking object and set it as current booking object
    const bookingCopy = cloneDeep(booking);
    await completeBooking(bookingCopy);
    console.log(
      "---- after compltion of booking JSON.parse(JSON.stringify(bookingCopy))"
    );
    // console.log(JSON.parse(JSON.stringify(bookingCopy)));
    this.updateBooking(bookingCopy);
  }

  @Action({ rawError: true })
  public async completeSelectedBookingPayment(accountingUUID: string) {
    const booking = this.bookings[this.selectedBookingUUID];

    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    // Update a copied booking object and set it as current booking object
    const bookingCopy = cloneDeep(booking);
    completeBookingPayment(bookingCopy, accountingUUID);
    this.updateBooking(bookingCopy);
  }

  @Action({ rawError: true })
  public async completeSelectedBookingAccountings(accountingsUuid?: string[]) {
    console.log("hi from completeSelectedBookingAccountings()");
    // We complete accountings of the booking if there is no uuid supplied
    // we complete corresponding accountings if their uuids are supplied
    const booking = this.bookings[this.selectedBookingUUID];

    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }
    if (!accountingsUuid) {
      // Update a copied booking object and set it as current booking object
      const bookingCopy = cloneDeep(booking);
      // we don't consider accountings created by backend to be completed by POS ... there is no such a scenario
      completeAccountingsBooking(
        bookingCopy.accountings.filter((acc) => !acc.id)
      );
      this.updateBooking(bookingCopy);
    } else {
      // Update a copied booking object and set it as current booking object
      const accountingsUuidsConcat = accountingsUuid.join(" - ");
      const bookingCopy = cloneDeep(booking);
      const accountingsToComplete = bookingCopy.accountings.filter((acc) =>
        accountingsUuidsConcat.includes(String(acc.uuid))
      );
      completeAccountingsBooking(accountingsToComplete);
      this.updateBooking(bookingCopy);
    }
  }

  @Action({ rawError: true })
  public async clearBooking() {
    console.log("-------- hi from clearBooking()");
    const booking = this.selectedBooking;
    console.log("booking");
    console.log(booking);

    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    const newBooking = createBooking();
    newBooking.currency = booking.currency;

    // TODO release all timeslots and optimize
    for (const pss of booking.pssList) {
      await releaseTimeslots({ id: pss.timeSlotId as number, count: 1 });
    }

    this.removeBookingFromList(booking);
    // Now if the booking in some moment of its life the user selected a seat from a chart without confirming its PSS
    // then we should make its holdToken expires immediately so that seats io release the held seats (only held seats, booked seats will not be released by this)
    if (booking.seatsIoHoldToken && booking.seatsIoHoldToken.holdToken) {
      const res = await makeHoldTokenExpire(booking.seatsIoHoldToken.holdToken);
    }

    // Now lets check if this booking has external vouchers and remove them from the module
    console.log(
      "Now lets check if this booking has external vouchers and remove them from the module"
    );
    clearCurrentSessionExternalVouchers(
      String(BookingModule.currentOrderSessionId)
    );

    // Choose the next booking in the list
    if (this.bookingList.length) {
      const newSelectedBooking = this.bookingList[0];
      this.selectBooking(newSelectedBooking.uuid);
      return;
    }

    // No bookings in list, create a new one, select it and add its uuid to pendingSync List;
    this.addBooking(newBooking);
    this.selectBooking(newBooking.uuid as string);
    this.setBookingStep({
      bookingUUID: String(newBooking.uuid),
      step: BookingStep.Order,
    });
  }

  // Clear all bookings from module
  @Action({ rawError: true })
  public async clearAllBookings() {
    // clear all bookings and their related data
    this.clearAllBookingsData();
    // pssExtensions are also no longer needed, so let's clear them
    PSSModule.setPSSExtensions({});
  }

  @Action({ commit: "SET_CALC_VALUE", rawError: true })
  public setCalcValue(calcValue: string) {
    return calcValue;
  }

  @Action({ rawError: true })
  public async initModule() {
    // Ensure that we have at least 1 booking selected
    /*
    WE DO NOT CREATE A NEW BOOKING WHEN BOOKING MODULE IS INITIALIZED
    WE DO SO WHEN THE USER SELECTS A POS CONFIG
    const selectedBookingUUID = this.selectedBookingUUID;
    const bookingList = this.bookingList;

    if (!selectedBookingUUID && !bookingList.length) {
      const booking = createBooking();
      const bookingUUID = booking.uuid as string;

      this.addBooking(booking);
      this.selectBooking(bookingUUID);
      this.setBookingStep({ [bookingUUID]: BookingStep.Order });
    }
    */
  }

  @Action({ rawError: true })
  public async createNewBooking() {
    const booking = createBooking();
    const bookingUUID = booking.uuid as string;

    this.addBooking(booking);
    this.increaseBookingCount();
    this.selectBooking(bookingUUID);
    this.setBookingStep({ bookingUUID, step: BookingStep.Order });

    // start a new transaction
    await startTransaction(bookingUUID);
  }

  @Action({ rawError: true })
  public deletePSS(data: { bookingUUID: string; criteria: IRemovePSS }) {
    this.removePSS(data);
  }

  @Action({ rawError: true })
  public removeBooking(booking: IProductSlotBooking) {
    console.log("-------- hi from removeBooking()");
    this.removeBookingFromList(booking);
    // clean all data related to this booking
    const uuid = booking.uuid;
    if (!uuid) {
      throw new Error(PSBErrors.HasNoUUID);
    }
    PSSModule.removePSSExtensionsByBooking(booking);
    this.removeAmountTracking(uuid);
    this.removeDiscountTracking(uuid);
    this.removePromocodes(uuid);
    this.removeBookingStep(uuid);
    this.removeAdyenResponseStatusTrack(uuid);
    this.clearBookingRefundListeners(uuid);
    this.clearBookingWaivers(uuid);
    this.clearBookingChanges(uuid);
    // Lets clear its Offers tracking
    OffersModule.deleteBookingOffersTracking(uuid);

    // Now lets check if this booking has external vouchers and remove them from the module
    if (
      ExternalVouchersModule.externalVouchers.length > 0 &&
      ExternalVouchersModule.externalVouchers.find(
        (v) => v.orderSessionId === BookingModule.currentOrderSessionId
      )
    ) {
      ExternalVouchersModule.removeExternalVouchersByOrderSession(
        String(BookingModule.currentOrderSessionId)
      );
    }
  }

  @Action
  async selectBooking(bookingUUID: string) {
    const booking = this.bookings[bookingUUID];

    if (!booking) {
      throw new Error(PSBErrors.NoBookingInModule);
    }

    let suspendBookingUUID: string | undefined;
    const previousUUID = this.selectedBookingUUID;

    if (
      previousUUID &&
      this.bookings[previousUUID] &&
      this.currentOrderSessionId
    ) {
      const previousBooking = this.bookings[previousUUID];
      const orderSession = previousBooking.orderSessions.find(
        (session) => session.id === this.currentOrderSessionId
      );

      const isIncomplete =
        orderSession &&
        !orderSession.signatureInfoFR &&
        orderSession.state !== OrderSessionState.COMPLETED &&
        this.bookingsSteps[previousUUID] !== BookingStep.CompleteOrder;

      if (isIncomplete) {
        LogService.info(
          `Booking ${previousUUID} is not signed nor completed yet, setting it to suspended`
        );
        suspendBookingUUID = previousUUID;
      }
    }

    const sortedSessions = booking.orderSessions.sort(
      (a, b) =>
        dayjs(b.sessionStart).valueOf() - dayjs(a.sessionStart).valueOf()
    );
    const newestSession = sortedSessions[0];

    // Commit state update
    this.context.commit("selectBookingMutation", {
      bookingUUID,
      currentOrderSessionId: newestSession.id,
      suspendBookingUUID,
    });

    // Handle side-effects after mutation

    // France only: suspend the previous receipt when switching away
    if (suspendBookingUUID) {
      try {
        await processAdditionalEvents(EventType.SUSPEND_RECEIPT);
      } catch (err) {
        LogService.info(
          `Error while processing suspend receipt event for booking ${suspendBookingUUID}`,
          { err }
        );
      }
    }

    // France only: resume a suspended receipt when switching back
    if (
      booking.isSuspended &&
      this.bookingsSteps[bookingUUID] !== BookingStep.CompleteOrder
    ) {
      LogService.info(
        `Newly selected booking ${bookingUUID} is suspended, setting it to resumed`
      );
      try {
        await processAdditionalEvents(EventType.RESUME_RECEIPT);
      } catch (err) {
        LogService.info(
          `Error while processing resume receipt event for booking ${bookingUUID}`,
          { err }
        );
      }
    }

    // Germany only: start a new sale event for the selected booking
    const currentOrderSession = booking.orderSessions
      .slice()
      .sort(
        (a, b) =>
          dayjs(b.sessionStart).valueOf() - dayjs(a.sessionStart).valueOf()
      )
      .find((session) => session.state === OrderSessionState.PENDING);
    if (currentOrderSession && bookingUUID === this.selectedBookingUUID) {
      processStartSaleEvent(booking, currentOrderSession.id).catch((error) => {
        LogService.info("Error processing start sale event", { error });
      });
    }
  }
}

const BookingModule = getModule(BookingStoreModule);
export default BookingModule;
